/**
 * Length units for artboard / canvas size entry.
 * Storage and Fabric remain pixels; physical units convert via DPI.
 */

export type LengthUnit = 'px' | 'in' | 'cm' | 'mm' | 'ft' | 'm';

export const LENGTH_UNITS: readonly LengthUnit[] = [
  'px',
  'in',
  'cm',
  'mm',
  'ft',
  'm',
] as const;

export const LENGTH_UNIT_LABELS: Record<LengthUnit, string> = {
  px: 'Pixels (px)',
  in: 'Inches (in)',
  cm: 'Centimeters (cm)',
  mm: 'Millimeters (mm)',
  ft: 'Feet (ft)',
  m: 'Meters (m)',
};

export const DEFAULT_DPI = 300;
export const MIN_DPI = 72;
export const MAX_DPI = 600;

const CM_PER_IN = 2.54;
const MM_PER_IN = 25.4;
const IN_PER_FT = 12;
const IN_PER_M = 100 / CM_PER_IN; // ≈ 39.3700787

export function clampDpi(dpi: number): number {
  if (!Number.isFinite(dpi)) return DEFAULT_DPI;
  return Math.min(MAX_DPI, Math.max(MIN_DPI, Math.round(dpi)));
}

/** Convert a length in the given unit to inches (px uses dpi). */
export function inchesFrom(value: number, unit: LengthUnit, dpi: number): number {
  if (!Number.isFinite(value)) return 0;
  const d = clampDpi(dpi);
  switch (unit) {
    case 'px':
      return value / d;
    case 'in':
      return value;
    case 'cm':
      return value / CM_PER_IN;
    case 'mm':
      return value / MM_PER_IN;
    case 'ft':
      return value * IN_PER_FT;
    case 'm':
      return value * IN_PER_M;
    default:
      return value / d;
  }
}

/** Convert inches to the given unit (px uses dpi). */
export function fromInches(inches: number, unit: LengthUnit, dpi: number): number {
  if (!Number.isFinite(inches)) return 0;
  const d = clampDpi(dpi);
  switch (unit) {
    case 'px':
      return inches * d;
    case 'in':
      return inches;
    case 'cm':
      return inches * CM_PER_IN;
    case 'mm':
      return inches * MM_PER_IN;
    case 'ft':
      return inches / IN_PER_FT;
    case 'm':
      return inches / IN_PER_M;
    default:
      return inches * d;
  }
}

export function toPixels(value: number, unit: LengthUnit, dpi: number): number {
  if (unit === 'px') {
    return Number.isFinite(value) ? Math.round(value) : 0;
  }
  return Math.round(inchesFrom(value, unit, dpi) * clampDpi(dpi));
}

export function fromPixels(px: number, unit: LengthUnit, dpi: number): number {
  if (unit === 'px') {
    return Number.isFinite(px) ? px : 0;
  }
  return fromInches(px / clampDpi(dpi), unit, dpi);
}

/** Re-express a value when switching units (same physical length). */
export function convertLength(
  value: number,
  fromUnit: LengthUnit,
  toUnit: LengthUnit,
  dpi: number
): number {
  if (fromUnit === toUnit) return value;
  const inches = inchesFrom(value, fromUnit, dpi);
  const next = fromInches(inches, toUnit, dpi);
  return toUnit === 'px' ? Math.round(next) : next;
}

/** Re-express when DPI changes (value stays in the same unit). */
export function convertLengthForDpiChange(
  value: number,
  unit: LengthUnit,
  oldDpi: number,
  newDpi: number
): number {
  if (unit === 'px') {
    // Keep physical size: px' = px * (newDpi / oldDpi)
    const inches = inchesFrom(value, 'px', oldDpi);
    return Math.round(fromInches(inches, 'px', newDpi));
  }
  // Physical units: displayed number stays the same
  return value;
}

export function formatLength(value: number, unit: LengthUnit): string {
  if (!Number.isFinite(value)) return '0';
  if (unit === 'px') return String(Math.round(value));
  if (unit === 'mm') {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }
  if (unit === 'ft' || unit === 'm') {
    return (Math.round(value * 1000) / 1000).toFixed(3).replace(/\.?0+$/, '');
  }
  // in, cm
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

export function formatPixelPair(widthPx: number, heightPx: number): string {
  return `${Math.round(widthPx)} × ${Math.round(heightPx)} px`;
}

/** Input step for number fields by unit. */
export function inputStepForUnit(unit: LengthUnit): number | 'any' {
  switch (unit) {
    case 'px':
      return 1;
    case 'mm':
      return 0.1;
    case 'ft':
    case 'm':
      return 0.001;
    default:
      return 0.01;
  }
}
